import type { DateRangeId } from "../lib/storage";

interface Option {
  id: DateRangeId;
  label: string;
  /** Number of trading days back from "today". `Infinity` = all. */
  days: number;
}

export const RANGE_OPTIONS: readonly Option[] = [
  { id: "all", label: "All", days: Infinity },
  { id: "5y", label: "5J", days: 5 * 365 },
  { id: "3y", label: "3J", days: 3 * 365 },
  { id: "1y", label: "1J", days: 365 },
  { id: "6m", label: "6M", days: 182 },
  { id: "3m", label: "3M", days: 91 },
  { id: "1m", label: "1M", days: 30 },
  { id: "1w", label: "1W", days: 7 },
];

interface Props {
  active: DateRangeId;
  onChange: (id: DateRangeId) => void;
}

/**
 * Segmented control for picking the visible chart window. Selecting any
 * option resets the range slider's zoom (handled by the parent).
 */
export function DateRangeControl({ active, onChange }: Props) {
  return (
    <div
      role="radiogroup"
      aria-label="Zeitraum"
      className="inline-flex rounded-md bg-slate-100 dark:bg-slate-800 text-[11px] sm:text-xs overflow-x-auto"
    >
      {RANGE_OPTIONS.map((opt) => {
        const isActive = opt.id === active;
        return (
          <button
            key={opt.id}
            role="radio"
            aria-checked={isActive}
            type="button"
            onClick={() => onChange(opt.id)}
            className={
              "shrink-0 px-2 sm:px-2.5 py-1 rounded-md font-medium transition whitespace-nowrap " +
              (isActive
                ? "bg-white dark:bg-slate-950 text-slate-900 dark:text-white shadow-sm ring-1 ring-slate-200 dark:ring-slate-700"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100")
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Convert a range-id + the data's actual span into [startMs, endMs]. */
export function resolveRangeMs(
  range: DateRangeId,
  dataStartMs: number,
  dataEndMs: number,
): { start: number; end: number } {
  const opt = RANGE_OPTIONS.find((o) => o.id === range) ?? RANGE_OPTIONS[0];
  if (!Number.isFinite(opt.days)) return { start: dataStartMs, end: dataEndMs };
  const start = dataEndMs - opt.days * 24 * 3600 * 1000;
  return { start: Math.max(dataStartMs, start), end: dataEndMs };
}
